/**
 * Admin · Einstellungen · Watchdog — die Scoring-Verwaltung (FR-6)
 *
 * Bauplan Check and Score v1.2, Teil 3.2: Der Fachbereich pflegt hier Meta und
 * Texte selbst, ohne dass jemand Code deployen muss.
 *
 *   FR-6.1  Tabelle aller Scorings: Code, Kategorie, severity, scope, active, Klartext
 *   FR-6.2  Editierbar sind Texte und severity — NICHT scoring_code (Kopplung an
 *           die Regel-Registry) und NICHT scope (bestimmt, wie viele
 *           Ergebnis-Zeilen ein Lauf erzeugt)
 *   FR-6.3  active-Schalter wirkt ab dem naechsten Score Run · kein Loeschen
 *
 * Schalter „DigA-Freigabe notwendig" je Check (Francesco und Mario, 29.10.2026,
 * `review_required`): An = ein durchgefallener Check wartet im Notification-
 * Center auf die Freigabe, bevor ambix ihn zeigt (wie bisher). Aus = der Befund
 * geht sofort an ambix; die Meldung im Center entsteht trotzdem. Wirkt sofort,
 * auch auf offene Meldungen und den Verlauf, weil Tuer 2 den aktuellen Wert liest.
 *
 * Der Schalter „gebaut" ist keine Kosmetik: Ein aktives Scoring ohne Funktion in
 * der Registry wird beim Score Run still uebersprungen. Hier sieht man das,
 * bevor man sich ueber fehlende Ergebnisse wundert.
 *
 * Frueher stand an dieser Stelle die Template-Zuordnung je Datenbank. Die ist am
 * 21.08.2026 entfallen — der Score Run bewertet ueber die Regel-Registry, nicht
 * ueber ein Scan-Template.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Alert, Button, Chip, CircularProgress, Snackbar, Stack, Switch,
  Table, TableHead, TableBody, TableRow, TableCell, TextField, Tooltip, IconButton,
  Collapse, Select, MenuItem, InputAdornment, LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { api } from '../../../api/client.js';
import { colors } from '../../../styles/theme.js';
import RecommendedFocusEventsCard from './RecommendedFocusEventsCard.js';

interface ScoringDefinition {
  scoring_code: string;
  question: string;
  explanation: string;
  impact_text: string | null;
  scoring_type: string;
  scope: string;
  category: string;
  severity: 'showstopper' | 'warning' | 'notice';
  raw_sources: string;
  active: boolean;
  /** Schalter „DigA-Freigabe notwendig" (Migration 20261030_01); true, solange die Spalte fehlt. */
  review_required: boolean;
  implemented: boolean;
}

/** Die severity-Skala bewertet Scorings — bewusst andere Farbwelt als job_status. */
const SEVERITY_META: Record<ScoringDefinition['severity'], { label: string; color: string }> = {
  showstopper: { label: 'Showstopper', color: colors.scoreRed },
  warning: { label: 'Warning', color: '#ea580c' },
  notice: { label: 'Hinweis', color: '#ca8a04' },
};

const CATEGORY_LABEL: Record<string, string> = {
  tracking_quality: 'Tracking-Qualität',
  privacy: 'Datenschutz',
  marketing_tags: 'Marketing-Tags',
};

const SCOPE_HINT: Record<string, string> = {
  domain: 'ein Ergebnis pro Lauf',
  target: 'ein Ergebnis pro Event × Kanal-Ziel',
};

/** Was der Fachbereich ändern darf (FR-6.2). */
interface Draft {
  severity: ScoringDefinition['severity'];
  question: string;
  explanation: string;
  impact_text: string;
}

const toDraft = (d: ScoringDefinition): Draft => ({
  severity: d.severity,
  question: d.question ?? '',
  explanation: d.explanation ?? '',
  impact_text: d.impact_text ?? '',
});

const isDirty = (a: Draft, b: Draft): boolean =>
  a.severity !== b.severity || a.question !== b.question
  || a.explanation !== b.explanation || a.impact_text !== b.impact_text;

/** Die Felder des Entwurfs; nur wenn eines davon gesendet wurde, wird der Entwurf der Zeile neu gesetzt. */
const DRAFT_FIELDS: (keyof Draft)[] = ['severity', 'question', 'explanation', 'impact_text'];

export default function WatchdogSettingsPage() {
  const [definitions, setDefinitions] = useState<ScoringDefinition[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    const res = await api.get<{ definitions: ScoringDefinition[] }>('/watchdog/scoring-definitions');
    setLoading(false);
    if (!res.success) { setError(res.error.message); return; }
    setDefinitions(res.data.definitions);
    setDrafts(Object.fromEntries(res.data.definitions.map((d) => [d.scoring_code, toDraft(d)])));
    setError(null);
  };

  useEffect(() => { void load(); }, []);

  const patch = async (code: string, body: Record<string, unknown>, message: string) => {
    setBusy(code);
    const res = await api.patch<{ definition: ScoringDefinition }>(`/watchdog/scoring-definitions/${code}`, body);
    setBusy(null);
    if (!res.success) { setError(res.error.message); return; }

    setDefinitions((list) => list.map((d) => (d.scoring_code === code
      ? { ...d, ...res.data.definition, implemented: d.implemented }
      : d)));
    // Entwurf nur neu setzen, wenn Texte gespeichert wurden: ein Schalter (Aktiv,
    // DigA-Freigabe notwendig) darf ungespeicherte Eingaben nicht verwerfen.
    if (DRAFT_FIELDS.some((field) => field in body)) {
      setDrafts((prev) => ({ ...prev, [code]: toDraft({ ...res.data.definition, implemented: true }) }));
    }
    setToast(message);
    setError(null);
  };

  const toggleActive = (d: ScoringDefinition) =>
    patch(d.scoring_code, { active: !d.active },
      `„${d.scoring_code}" ist ab dem nächsten Score Run ${d.active ? 'abgeschaltet' : 'aktiv'}.`);

  const toggleReviewRequired = (d: ScoringDefinition) =>
    patch(d.scoring_code, { review_required: !d.review_required },
      d.review_required
        ? `„${d.scoring_code}": Befunde gehen ab sofort ohne Freigabe an ambix.`
        : `„${d.scoring_code}": Befunde warten ab sofort auf die DigA-Freigabe.`);

  const saveTexts = (d: ScoringDefinition) => {
    const draft = drafts[d.scoring_code];
    if (!draft) return;
    void patch(d.scoring_code, {
      severity: draft.severity,
      question: draft.question,
      explanation: draft.explanation,
      impact_text: draft.impact_text || null,
    }, `„${d.scoring_code}" gespeichert.`);
  };

  const resetDraft = (d: ScoringDefinition) =>
    setDrafts((prev) => ({ ...prev, [d.scoring_code]: toDraft(d) }));

  const toggleRow = (code: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });

  const gefiltert = useMemo(() => {
    const n = search.trim().toLowerCase();
    if (!n) return definitions;
    return definitions.filter((d) =>
      d.scoring_code.toLowerCase().includes(n)
      || (d.question ?? '').toLowerCase().includes(n)
      || CATEGORY_LABEL[d.category]?.toLowerCase().includes(n));
  }, [definitions, search]);

  const aktiv = definitions.filter((d) => d.active).length;
  const ohneRegel = definitions.filter((d) => d.active && !d.implemented);

  return (
    <Box sx={{ maxWidth: 1300 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Scoring-Verwaltung</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Welche Prüfungen der Watchdog rechnet und wie schwer ein Befund wiegt. Änderungen
        wirken ab dem <strong>nächsten Score Run</strong> — gelöscht wird nie, nur abgeschaltet.
        „DigA-Freigabe notwendig" bestimmt, ob ein Befund erst nach Freigabe im
        Notification-Center bei ambix erscheint (Tracking-Ansicht und Agents); dieser Schalter
        wirkt <strong>sofort</strong>.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {ohneRegel.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          {ohneRegel.length} aktive(s) Scoring ohne Regel im Code:{' '}
          <strong>{ohneRegel.map((d) => d.scoring_code).join(', ')}</strong>. Der Score Run
          überspringt sie — sie erzeugen keine Ergebnisse.
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Scoring suchen …"
          size="small"
          sx={{ maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: colors.black60 }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Chip label={`${aktiv} von ${definitions.length} aktiv`} size="small" sx={{ fontWeight: 600 }} />
      </Stack>

      {busy && <LinearProgress sx={{ mb: 1 }} />}

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : gefiltert.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {definitions.length === 0
                ? 'Keine Scoring-Definitionen in dieser Umgebung. Ist die Tabelle twd_scoring_definitions geseedet?'
                : `Kein Scoring passt zu „${search}".`}
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ width: 44 }} />
                <TableCell sx={{ fontWeight: 700 }}>Prüfung</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Kategorie</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Schwere</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Umfang</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Aktiv</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">DigA-Freigabe notwendig</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gefiltert.map((d) => {
                const draft = drafts[d.scoring_code] ?? toDraft(d);
                const dirty = isDirty(draft, toDraft(d));
                const offen = open.has(d.scoring_code);
                const sev = SEVERITY_META[d.severity] ?? SEVERITY_META.notice;

                return (
                  <Fragment key={d.scoring_code}>
                    <TableRow hover sx={{ opacity: d.active ? 1 : 0.55 }}>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleRow(d.scoring_code)}>
                          {offen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>

                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleRow(d.scoring_code)}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                            {d.scoring_code}
                          </Typography>
                          {!d.implemented && (
                            <Tooltip title="Zu diesem Code gibt es keine Funktion in der Regel-Registry — der Score Run überspringt ihn.">
                              <Chip label="keine Regel" size="small" color="warning" variant="outlined" sx={{ height: 19, fontSize: '0.65rem' }} />
                            </Tooltip>
                          )}
                          {dirty && <Chip label="ungespeichert" size="small" sx={{ height: 19, fontSize: '0.65rem' }} />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{d.question}</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="caption">{CATEGORY_LABEL[d.category] ?? d.category}</Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={sev.label}
                          size="small"
                          sx={{ bgcolor: sev.color, color: colors.white, fontWeight: 600, fontSize: '0.68rem', height: 21 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Tooltip title={SCOPE_HINT[d.scope] ?? ''}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{d.scope}</Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title={d.active ? 'Abschalten — wirkt ab dem nächsten Score Run' : 'Einschalten'}>
                          <span>
                            <Switch
                              checked={d.active}
                              onChange={() => void toggleActive(d)}
                              disabled={busy === d.scoring_code}
                              size="small"
                            />
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip
                          title={d.review_required
                            ? 'An: ein Befund wartet im Notification-Center auf die Freigabe, bevor ambix ihn zeigt. Ausschalten wirkt sofort.'
                            : 'Aus: Befunde gehen ohne Freigabe sofort an ambix. Einschalten wirkt sofort.'}
                        >
                          <span>
                            <Switch
                              checked={d.review_required}
                              onChange={() => void toggleReviewRequired(d)}
                              disabled={busy === d.scoring_code}
                              size="small"
                              inputProps={{ 'aria-label': `DigA-Freigabe notwendig für ${d.scoring_code}` }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* ── Details und Texte ─────────────────── */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                        <Collapse in={offen} unmountOnExit>
                          <Box sx={{ py: 2, pl: 5, pr: 2 }}>
                            <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                                  Datenquellen
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                  {d.raw_sources}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                                  Art
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                                  {d.scoring_type === 'comparison' ? 'Vergleich gegen das Scanprofil' : 'Bewertung des Ist-Zustands'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                                  Regel im Code
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                  {d.implemented ? `${d.scoring_code}()` : '— fehlt —'}
                                </Typography>
                              </Box>
                            </Stack>

                            <Stack spacing={2}>
                              <Box sx={{ maxWidth: 420 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Schwere
                                </Typography>
                                <Select
                                  value={draft.severity}
                                  size="small"
                                  fullWidth
                                  onChange={(e) => setDrafts((p) => ({
                                    ...p,
                                    [d.scoring_code]: { ...draft, severity: e.target.value as ScoringDefinition['severity'] },
                                  }))}
                                  sx={{ mt: 0.5 }}
                                >
                                  {(Object.keys(SEVERITY_META) as ScoringDefinition['severity'][]).map((s) => (
                                    <MenuItem key={s} value={s}>{SEVERITY_META[s].label}</MenuItem>
                                  ))}
                                </Select>
                              </Box>

                              <TextField
                                label="Frage — was wird geprüft?"
                                value={draft.question}
                                onChange={(e) => setDrafts((p) => ({ ...p, [d.scoring_code]: { ...draft, question: e.target.value } }))}
                                size="small" fullWidth
                              />
                              <TextField
                                label="Erklärung — wie wird geprüft?"
                                value={draft.explanation}
                                onChange={(e) => setDrafts((p) => ({ ...p, [d.scoring_code]: { ...draft, explanation: e.target.value } }))}
                                size="small" fullWidth multiline minRows={2}
                              />
                              <TextField
                                label="Klartext — warum ein Befund ein Problem ist"
                                value={draft.impact_text}
                                onChange={(e) => setDrafts((p) => ({ ...p, [d.scoring_code]: { ...draft, impact_text: e.target.value } }))}
                                size="small" fullWidth multiline minRows={2}
                                helperText="Dieser Text steht später im Notification-Center und im Slack-Alarm."
                              />
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                              <Button
                                variant="contained" size="small" startIcon={<SaveIcon />}
                                onClick={() => saveTexts(d)}
                                disabled={!dirty || busy === d.scoring_code}
                              >
                                Speichern
                              </Button>
                              <Button
                                size="small" startIcon={<UndoIcon />}
                                onClick={() => resetDraft(d)}
                                disabled={!dirty || busy === d.scoring_code}
                              >
                                Verwerfen
                              </Button>
                              <Box sx={{ flex: 1 }} />
                              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                Code und Umfang sind fest — sie hängen an der Regel im Backend.
                              </Typography>
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Go-Live-Checkliste Nr. 18: die Vorauswahl des ambix-Wizards, je Umgebung. */}
      <Box sx={{ mt: 3 }}>
        <RecommendedFocusEventsCard />
      </Box>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
